/**
 * Ritual bundle builder: auto 2 or 3 products — pouch at 2, pouch + % at 3.
 */
class ShopBundle extends HTMLElement {
  constructor() {
    super();
    /** @type {null | { variantId: number; title: string; unitPriceCents: number; imageUrl: string; demo?: boolean }}[]} */
    this.slots = [null, null, null];
    this.maxSlots = 3;
    this.minSlots = 2;
  }

  connectedCallback() {
    this.slotsContainer = this.querySelector('[data-bundle-slots]');
    this.packagingEl = this.querySelector('[data-bundle-packaging]');
    this.bagEl = this.querySelector('[data-bundle-bag]');
    this.bagFreeEl = this.querySelector('[data-bundle-bag-free]');
    this.pricingEl = this.querySelector('[data-bundle-pricing]');
    this.subtotalWrap = this.querySelector('[data-bundle-subtotal]');
    this.subtotalLabelEl = this.querySelector('[data-bundle-subtotal-label]');
    this.subtotalValueEl = this.querySelector('[data-bundle-subtotal-value]');
    this.discountWrap = this.querySelector('[data-bundle-discount]');
    this.discountLabelEl = this.querySelector('[data-bundle-discount-label]');
    this.discountValueEl = this.querySelector('[data-bundle-discount-value]');
    this.totalValueEl = this.querySelector('[data-bundle-total-value]');
    this.primaryBtn = this.querySelector('[data-bundle-primary]');
    this.primaryTextEl = this.querySelector('[data-bundle-primary-text]');
    this.catalogEl = this.querySelector('#bundle-catalog');
    this.discountThreePercent = parseInt(this.dataset.discountThreePercent || '5', 10);

    this.querySelectorAll('[data-bundle-add]').forEach((btn) => {
      btn.addEventListener('click', () => this.handleAdd(btn));
    });

    if (this.primaryBtn) {
      this.primaryBtn.addEventListener('click', () => this.handlePrimary());
    }

    if (this.slotsContainer) {
      this.slotsContainer.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-bundle-slot-remove]');
        if (!removeBtn) return;
        const index = parseInt(removeBtn.dataset.bundleSlotRemove, 10);
        if (Number.isFinite(index)) this.clearSlot(index);
      });
    }

    this.syncState();
  }

  getFilledCount() {
    return this.slots.filter((slot) => slot !== null).length;
  }

  getBundleSize() {
    return this.getFilledCount();
  }

  isComplete() {
    return this.getFilledCount() >= this.minSlots;
  }

  isFull() {
    return this.getFilledCount() >= this.maxSlots;
  }

  getFilledSlots() {
    return this.slots.filter((slot) => slot !== null);
  }

  slotIndexForVariant(variantId) {
    for (let i = 0; i < this.maxSlots; i += 1) {
      if (this.slots[i] && this.slots[i].variantId === variantId) return i;
    }
    return -1;
  }

  demoIndexInSlots(demoIndex) {
    const vid = -parseInt(demoIndex, 10);
    return this.slotIndexForVariant(vid);
  }

  firstEmptySlotIndex() {
    if (this.isFull()) return -1;
    for (let i = 0; i < this.maxSlots; i += 1) {
      if (!this.slots[i]) return i;
    }
    return -1;
  }

  /**
   * @param {HTMLElement} btn
   */
  handleAdd(btn) {
    const card = btn.closest('[data-bundle-product]');
    if (!card || card.classList.contains('bundle__product-card--unavailable')) return;

    const variantId = parseInt(card.dataset.variantId, 10);
    const demoIndex = card.dataset.demoProduct;

    if (!variantId && demoIndex) {
      if (this.demoIndexInSlots(demoIndex) >= 0) return;
      const slotIndex = this.firstEmptySlotIndex();
      if (slotIndex < 0) return;

      this.slots[slotIndex] = {
        variantId: -parseInt(demoIndex, 10),
        title: (card.dataset.productTitle || '').trim(),
        unitPriceCents: parseInt(card.dataset.variantPrice || '240000', 10) || 240000,
        imageUrl: '',
        demo: true,
      };

      this.syncState();
      return;
    }

    if (!variantId) return;
    if (this.slotIndexForVariant(variantId) >= 0) return;

    const slotIndex = this.firstEmptySlotIndex();
    if (slotIndex < 0) return;

    this.slots[slotIndex] = {
      variantId,
      title: (card.dataset.productTitle || '').trim(),
      unitPriceCents: parseInt(card.dataset.variantPrice || '0', 10) || 0,
      imageUrl: card.dataset.productImage || '',
    };

    this.syncState();
  }

  clearSlot(index) {
    if (index < 0 || index >= this.slots.length) return;
    this.slots[index] = null;
    this.syncState();
  }

  syncState() {
    this.renderSlots();
    this.updateBenefitIndicators();
    this.updateBagVisual();
    this.updatePackagingHint();
    this.updatePricing();
    this.updateCatalogState();
    this.updatePrimaryButton();
  }

  updateBenefitIndicators() {
    const filled = this.getFilledCount();
    this.querySelectorAll('[data-bundle-benefit]').forEach((el) => {
      const tier = parseInt(el.dataset.bundleBenefit, 10);
      const active =
        (filled >= 3 && tier === 3) || (filled >= 2 && filled < 3 && tier === 2);
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  formatMoney(cents) {
    const n = Number(cents);
    if (!Number.isFinite(n)) return '';
    const fmt = this.dataset.moneyFormat;
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(n, fmt || Shopify.money_format);
    }
    return (n / 100).toFixed(2);
  }

  getMoneySubtotalCents() {
    return this.getFilledSlots().reduce((sum, s) => sum + s.unitPriceCents, 0);
  }

  getDiscountPercent() {
    if (this.getFilledCount() === 3 && this.discountThreePercent > 0) {
      return this.discountThreePercent;
    }
    return 0;
  }

  getDiscountCents() {
    const subtotal = this.getMoneySubtotalCents();
    const pct = this.getDiscountPercent();
    if (pct <= 0) return 0;
    return Math.round(subtotal * pct / 100);
  }

  getMoneyTotalCents() {
    return this.getMoneySubtotalCents() - this.getDiscountCents();
  }

  updatePricing() {
    const subtotalCents = this.getMoneySubtotalCents();
    const discountCents = this.getDiscountCents();
    const discountPct = this.getDiscountPercent();
    const totalCents = this.getMoneyTotalCents();

    if (this.totalValueEl) {
      this.totalValueEl.textContent = this.formatMoney(totalCents);
    }

    if (this.pricingEl) {
      this.pricingEl.hidden = discountPct <= 0;
    }

    if (this.subtotalWrap && this.subtotalLabelEl && this.subtotalValueEl) {
      if (discountPct > 0) {
        this.subtotalWrap.hidden = false;
        this.subtotalLabelEl.textContent = this.dataset.i18nSubtotal || '';
        this.subtotalValueEl.textContent = this.formatMoney(subtotalCents);
      } else {
        this.subtotalWrap.hidden = true;
      }
    }

    if (this.discountWrap && this.discountLabelEl && this.discountValueEl) {
      if (discountPct > 0) {
        this.discountWrap.hidden = false;
        this.discountLabelEl.textContent = `${this.dataset.i18nDiscount || ''} (−${discountPct}%)`;
        this.discountValueEl.textContent = `−${this.formatMoney(discountCents)}`;
      } else {
        this.discountWrap.hidden = true;
      }
    }
  }

  updateBagVisual() {
    if (!this.bagEl) return;
    const filled = this.getFilledCount();
    const show = filled >= 2;
    this.bagEl.hidden = !show;
    if (this.bagFreeEl) {
      const lang = document.documentElement.lang || '';
      const isUk = lang.startsWith('uk') || lang === 'ua';
      this.bagFreeEl.textContent = isUk
        ? this.dataset.bagFreeUk || 'Безкоштовно'
        : this.dataset.bagFreeEn || 'Free';
    }
  }

  updatePackagingHint() {
    if (!this.packagingEl) return;
    const filled = this.getFilledCount();
    let type = 'none';
    if (filled === 2) type = 'pouch';
    if (filled >= 3) type = 'pouch_three';

    const map = {
      none: this.dataset.msgNone || '',
      pouch: this.dataset.msgPouch || '',
      pouch_three: this.dataset.msgPouchThree || '',
    };
    this.packagingEl.textContent = map[type] || '';
    this.packagingEl.hidden = !map[type];
    this.packagingEl.dataset.packagingType = type;
  }

  updateCatalogState() {
    const full = this.isFull();
    this.querySelectorAll('[data-bundle-product][data-variant-id]').forEach((card) => {
      const vid = parseInt(card.dataset.variantId, 10);
      const inBundle = this.slotIndexForVariant(vid) >= 0;
      const addBtn = card.querySelector('[data-bundle-add]');
      card.classList.toggle('bundle__product-card--in-bundle', inBundle);
      if (addBtn) {
        addBtn.disabled = inBundle || full;
      }
    });

    this.querySelectorAll('[data-bundle-product][data-demo-product]').forEach((card) => {
      const demoIndex = card.dataset.demoProduct;
      const inBundle = this.demoIndexInSlots(demoIndex) >= 0;
      const addBtn = card.querySelector('[data-bundle-add]');
      card.classList.toggle('bundle__product-card--in-bundle', inBundle);
      if (addBtn) {
        addBtn.disabled = inBundle || full;
      }
    });
  }

  updatePrimaryButton() {
    if (!this.primaryBtn || !this.primaryTextEl) return;
    const complete = this.isComplete();
    this.primaryTextEl.textContent = complete
      ? this.dataset.i18nAddBundle || ''
      : this.dataset.i18nSelectMore || '';
    this.primaryBtn.dataset.bundleAction = complete ? 'submit' : 'scroll';
  }

  handlePrimary() {
    if (this.isComplete()) {
      this.submit();
      return;
    }
    if (this.catalogEl) {
      this.catalogEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  renderSlots() {
    if (!this.slotsContainer) return;

    const emptyLabel = this.dataset.i18nSlotEmpty || 'Select product';
    const removeLabel = this.dataset.i18nRemove || 'Remove';
    const slotsHtml = [];

    for (let i = 0; i < this.maxSlots; i += 1) {
      const slot = this.slots[i];

      if (slot) {
        const img = slot.imageUrl
          ? `<img src="${this.escapeHtml(slot.imageUrl)}" alt="" loading="lazy">`
          : '';
        const priceHtml = slot.unitPriceCents > 0
          ? `<p class="bundle__slot-price">${this.escapeHtml(this.formatMoney(slot.unitPriceCents))}</p>`
          : '';
        slotsHtml.push(`
          <div class="bundle__slot bundle__slot--filled" data-slot-index="${i}">
            <div class="bundle__slot-image">${img}</div>
            <div class="bundle__slot-meta">
              <p class="bundle__slot-title">${this.escapeHtml(slot.title)}</p>
              ${priceHtml}
            </div>
            <button type="button" class="bundle__slot-remove" data-bundle-slot-remove="${i}" aria-label="${this.escapeHtml(removeLabel)}">×</button>
          </div>
        `);
      } else {
        slotsHtml.push(`
          <div class="bundle__slot bundle__slot--empty" data-slot-index="${i}">
            <span class="bundle__slot-placeholder">${this.escapeHtml(emptyLabel)}</span>
          </div>
        `);
      }
    }

    this.slotsContainer.innerHTML = slotsHtml.join('');
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  async submit() {
    if (!this.isComplete()) return;

    if (this.dataset.layoutOnly === '1') {
      window.alert(this.dataset.errLayoutOnly || 'Layout preview mode.');
      return;
    }

    const filled = this.getFilledSlots();
    const bundleSize = this.getBundleSize();
    const bundleId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `b-${Date.now()}`;
    const discountPct = this.getDiscountPercent();
    const pack = bundleSize === 3 && discountPct > 0 ? 'pouch_three' : 'pouch';
    const ritualPropKey = this.dataset.ritualPropKey || 'Ritual';
    const ritualPropValue =
      bundleSize >= 3 && discountPct > 0
        ? (this.dataset.ritualPropThree || `3 · alis bag · ${discountPct}%`).replace(
            /\[percent\]/g,
            String(discountPct)
          )
        : this.dataset.ritualPropTwo || '2 · alis bag';

    const items = filled.map((slot, idx) => {
      const properties = {
        _bundle_id: bundleId,
        _bundle_packaging: pack === 'pouch_three' ? 'pouch' : pack,
        _bundle_size: String(bundleSize),
        _bundle_line: String(idx + 1),
      };
      properties[ritualPropKey] = ritualPropValue;
      if (this.dataset.bagProductHandle) {
        properties._bag_product_handle = this.dataset.bagProductHandle;
      }
      if (discountPct > 0) {
        properties._bundle_discount_pct = String(discountPct);
      }
      return {
        id: slot.variantId,
        quantity: 1,
        properties,
      };
    });

    if (this.primaryBtn) {
      this.primaryBtn.disabled = true;
      this.primaryBtn.classList.add('btn--loading');
      const loader = this.primaryBtn.querySelector('[data-loader]');
      if (loader) loader.hidden = false;
    }

    try {
      const res = await fetch(`${window.routes.cart_add_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({ items }),
      });
      const data = await res.json();

      if (!res.ok || data.status) {
        const msg =
          (typeof data.description === 'string' && data.description) ||
          (typeof data.message === 'string' && data.message) ||
          this.dataset.errGeneric ||
          'Could not add to cart.';
        throw new Error(msg);
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle' });
      }

      window.location.href = window.routes.cart_url;
    } catch (e) {
      window.alert(e.message || String(e));
    } finally {
      if (this.primaryBtn) {
        this.primaryBtn.disabled = false;
        this.primaryBtn.classList.remove('btn--loading');
        const loader = this.primaryBtn.querySelector('[data-loader]');
        if (loader) loader.hidden = true;
      }
    }
  }
}

customElements.define('shop-bundle', ShopBundle);
